import type { PatientSearchItem } from "@elation/api-client/src/stitch-web/models";

import { Avatar, AvatarFallback } from "@src/shared/components/ui/avatar";
import { ComboboxItem } from "@src/shared/components/v2/Combobox";
import { cn } from "@src/shared/lib/utils";

import type { PatientSearchOption } from "../PatientSearch.types";

interface PatientSearchOptionRowProps {
  option: PatientSearchOption;
  isDisabled: boolean;
  isSelecting: boolean;
  selectingLabel: string;
  onSelect: (patient: PatientSearchItem) => void;
}

export function PatientSearchOptionRow({
  option,
  isDisabled,
  isSelecting,
  selectingLabel,
  onSelect,
}: PatientSearchOptionRowProps) {
  return (
    <ComboboxItem<PatientSearchOption>
      className={cn(
        "flex w-full items-center gap-2.5 rounded-none border-b border-border px-3 py-2.5 text-left transition-colors last:border-b-0 hover:bg-muted/50",
        isDisabled && "cursor-wait opacity-70 hover:bg-transparent",
      )}
      disabled={isDisabled}
      hideIndicator
      aria-disabled={isDisabled}
      onSelect={(selectedOption) => onSelect(selectedOption.patient)}
      value={option}
    >
      <Avatar className="size-8 border border-border bg-background">
        <AvatarFallback className="bg-primary/10 font-mono text-[10px] font-semibold text-primary">
          {option.initials}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-semibold text-foreground">
          {option.name}
        </p>
        <p className="mt-0.5 truncate font-mono text-[10px] text-muted-foreground">
          {option.metadata || option.helperText || "Patient"}
        </p>
      </div>
      {isSelecting ? (
        <span className="font-mono text-[10px] text-primary">
          {selectingLabel}
        </span>
      ) : null}
    </ComboboxItem>
  );
}
